import React, { useMemo } from "react";

import {
  faCalendar,
  faSearch,
  faWarning,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { validate as validateUUID } from "uuid";

import { DisplayDateTime } from "src/base-components/Date";
import { PillVariants } from "src/base-components/Pill";
import {
  PropertiesList,
  PropertiesListItem,
} from "src/base-components/PropertiesList";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { TagPill } from "src/base-components/TagPill";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { CaseBlock, CaseBlockProps } from "src/cases/caseView/CaseBlock";
import { CaseStatusPill } from "src/cases/common/CaseStatusPill";
import { useGetCasePropertyIcon } from "src/cases/common/useGetCasePropertyIcon";
import { useCase } from "src/cases/queries/cases";
import { getSubValueDisplayName } from "src/cases/utils";
import {
  CaseEnvironment,
  CasePropertiesValue,
} from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { EntityDetailsWindowPill } from "src/entities/entityView/EntityDetailsWindowPill";
import { useEntityDisplayValues } from "src/entities/useEntityDisplayValues";
import { Environment } from "src/hooks/useEnvironment";
import { formatNumber } from "src/utils/numbers";
import { assertUnreachable } from "src/utils/typeUtils";

type CaseDetailsBlockProps = Omit<CaseBlockProps, "children"> & {
  caseId: string | undefined | null;
  caseEnv: CaseEnvironment;
};

const renderEnum = (
  value: string,
  schema: Extract<CasePropertiesValue, { type: "enum" }>,
  caseProperties: Record<string, unknown>,
  propertyKey: string,
): React.ReactNode => {
  const enumValue = schema.values.find((v) => v.value === value);
  const subStatus = caseProperties.sub_status as string | undefined;

  if (propertyKey === "status") {
    return (
      <CaseStatusPill
        status={enumValue?.display_name || value}
        subStatus={
          getSubValueDisplayName(value, subStatus, schema) || subStatus
        }
        variant={enumValue?.color as PillVariants}
      />
    );
  }

  return <TagPill size="sm" value={enumValue || value} />;
};

const renderByType = ({
  value,
  schema,
  caseProperties,
  key,
  icon,
  caseEnv,
  entityDisplayMap,
}: {
  value: unknown;
  schema: CasePropertiesValue;
  caseProperties: Record<string, unknown>;
  key: string;
  icon?: IconDefinition;
  caseEnv?: CaseEnvironment;
  entityDisplayMap?: Record<string, string>;
}): React.ReactNode => {
  switch (schema.type) {
    case "string":
      if (typeof value === "string" && validateUUID(value)) {
        return <TruncatedIdPill feedback="inline-only-checkmark" id={value} />;
      }
      return <span className="font-inter-normal-12px">{String(value)}</span>;

    case "datetime":
      return (
        <span className="font-inter-normal-12px">
          {typeof value === "string" ? (
            <DisplayDateTime value={value} />
          ) : (
            String(value)
          )}
        </span>
      );

    case "tags":
      if (Array.isArray(value)) {
        return (
          <div className="flex flex-wrap gap-1">
            {value.map((tag, index) => {
              const tagValue = schema.values?.find((v) => v.value === tag);
              return <TagPill key={index} value={tagValue || String(tag)} />;
            })}
          </div>
        );
      }
      return <span className="font-inter-normal-12px">{String(value)}</span>;

    case "enum": {
      return renderEnum(String(value), schema, caseProperties, key);
    }

    case "event_ref": {
      return (
        <TruncatedIdPill feedback="inline-only-checkmark" id={String(value)} />
      );
    }

    case "entity_ref": {
      const entityId = value as string;
      return (
        <EntityDetailsWindowPill
          entityIcon={icon}
          environment={caseEnv as Environment}
          id={entityId}
          name={entityDisplayMap?.[entityId] || entityId}
          schemaId={schema.entity_type}
          isPinnable
        />
      );
    }

    case "integer":
    case "number":
      return (
        <span className="font-inter-normal-12px">
          {typeof value === "number" ? formatNumber(value) : String(value)}
        </span>
      );

    default:
      assertUnreachable(schema);
  }
};

const useCaseEntityDisplayMap = ({
  caseProperties,
  schemaProperties,
  environment,
}: {
  caseProperties: Record<string, unknown> | undefined;
  schemaProperties: Record<string, CasePropertiesValue> | undefined;
  environment: Environment;
}) => {
  const entityRefs = useMemo(() => {
    if (!caseProperties || !schemaProperties) return [];

    return Object.entries(schemaProperties)
      .filter(
        (
          entry,
        ): entry is [
          string,
          Extract<CasePropertiesValue, { type: "entity_ref" }>,
        ] => entry[1].type === "entity_ref",
      )
      .map(([key, schema]) => {
        const entityId = caseProperties[key];
        if (!entityId || typeof entityId !== "string") return null;
        return { id: entityId, entity_type: schema.entity_type };
      })
      .filter((ref): ref is NonNullable<typeof ref> => ref !== null);
  }, [caseProperties, schemaProperties]);

  return useEntityDisplayValues({
    entityRefs,
    environment,
    enabled: entityRefs.length > 0,
  });
};

export const CaseDetailsBlock: React.FC<CaseDetailsBlockProps> = ({
  caseId,
  caseEnv,
  ...props
}) => {
  const { data: caseData, isLoading, error: caseError } = useCase(caseId);
  const getPropertyIcon = useGetCasePropertyIcon();

  const template = caseData?.data.template;
  const templateVersion = caseData?.data.template_version;

  const env = caseEnv === CaseEnvironment.LIVE ? "live" : "sandbox";

  const { displayMap: entityDisplayMap } = useCaseEntityDisplayMap({
    caseProperties: caseData?.data.case_properties,
    schemaProperties: templateVersion?.case_properties,
    environment: env as Environment,
  });

  if (!caseId) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          dataLoc="case-details-no-case-id"
          description="This block currently has no linked case."
          headline="No case provided"
          icon={faSearch}
          size="compact"
        />
      </CaseBlock>
    );
  }

  if (isLoading) {
    return (
      <CaseBlock {...props}>
        <div className="flex h-full flex-col">
          <div className="mb-3" data-loc="case-details-description-loading">
            <SkeletonPlaceholder height="h-3.5" width="w-full" />
          </div>
          <PropertiesList isLoading={true} items={[]} loadingRows={5} />
        </div>
      </CaseBlock>
    );
  }

  if (!caseData || !templateVersion || !template || caseError) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          dataLoc="case-details-no-case-data"
          description="Unable to load case data"
          headline="Error loading case"
          icon={faWarning}
          size="compact"
          variant="error"
        />
      </CaseBlock>
    );
  }

  const caseProperties = caseData.data.case_properties || {};
  const schemaProperties = templateVersion.case_properties;

  const propertyItems: PropertiesListItem[] = Object.entries(caseProperties)
    .filter(([key]) => schemaProperties[key]) // Only show properties that exist in schema
    .map(([key, value]) => {
      const schema = schemaProperties[key];
      const icon = getPropertyIcon(schema);

      return {
        key,
        label: schema.display_name || key,
        icon,
        value: renderByType({
          value,
          schema,
          caseProperties,
          key,
          icon,
          caseEnv,
          entityDisplayMap,
        }),
      };
    });

  const createdAt = caseData.data.created_at;
  if (createdAt) {
    propertyItems.push({
      key: "created_at",
      label: "Case Created At",
      icon: faCalendar,
      value: (
        <span className="font-inter-normal-12px">
          <DisplayDateTime value={createdAt} />
        </span>
      ),
    });
  }

  return (
    <CaseBlock {...props}>
      <div className="flex h-full flex-col">
        <PropertiesList dataLocPrefix="case-details" items={propertyItems} />
      </div>
    </CaseBlock>
  );
};
